import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles, BookMarked, ChevronLeft, Trash2, FileDown, Download,
  Loader2, AlertTriangle, ScrollText, Lock, ArrowRight, Calendar,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   Mis consultas de Colega IA · /app/asistente-ia/mis-consultas
   Historial persistente con re-descarga PDF/Word sin regenerar.
   ═══════════════════════════════════════════════════════════════ */

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-CL", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
};

export default function MisConsultasIA() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // "pdf"|"docx"|null
  const [deletingId, setDeletingId] = useState(null);

  const isLawyer = user?.role === "lawyer";
  const isPremium = user?.plan && user.plan !== "free";

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/ai/mis-consultas");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isLawyer && isPremium) load(); }, [isLawyer, isPremium, load]);

  const openDetail = async (item) => {
    setDetailLoading(true); setSelected({ ...item });
    try {
      const { data } = await api.get(`/ai/mis-consultas/${item.id}`);
      setSelected(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSelected(null);
    } finally { setDetailLoading(false); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`¿Eliminar la consulta "${item.title}"? Esta acción no se puede deshacer.`)) return;
    setDeletingId(item.id);
    try {
      await api.delete(`/ai/mis-consultas/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (selected?.id === item.id) setSelected(null);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setDeletingId(null); }
  };

  const exportItem = async (item, fmt) => {
    if (exporting) return;
    setExporting(`${item.id}-${fmt}`);
    try {
      const token = localStorage.getItem("lexlink_token");
      const resp = await fetch(`${API}/ai/mis-consultas/${item.id}/export/${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      a.download = `colega-ia-${item.task}-${ts}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Fallo la exportación: ${err.message}`);
    } finally { setExporting(null); }
  };

  if (!isLawyer || !isPremium) {
    return (
      <div className="container-app py-16 max-w-2xl">
        <div className="panel p-8 text-center" data-testid="misc-restricted">
          <Lock size={30} className="mx-auto text-muted-foreground"/>
          <h1 className="serif text-2xl mt-4">Sección exclusiva</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Tu biblioteca personal de Colega IA está disponible sólo para abogados con membresía activa.
          </p>
          <Link to="/app/asistente-ia" className="btn-ghost mt-6 inline-flex">Volver</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-10 max-w-6xl colega-ia-font" data-testid="mis-consultas-page">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/app/asistente-ia" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="misc-back">
          <ChevronLeft size={12}/> Volver a Colega IA
        </Link>
      </div>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400/25 to-cyan-400/20 border border-emerald-400/40 flex items-center justify-center">
          <BookMarked size={22} className="text-emerald-300"/>
        </div>
        <div>
          <div className="label-eyebrow text-emerald-300">Biblioteca personal</div>
          <h1 className="serif text-4xl mt-1">Mis consultas</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            Historial completo de tus consultas a <strong className="text-foreground">Colega IA</strong>. Puedes re-descargarlas en PDF o Word
            en cualquier momento sin volver a consumir tokens de IA.
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="misc-error">
          <AlertTriangle size={13}/> {error}
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        {/* Lista */}
        <div className="lg:col-span-2 space-y-3" data-testid="misc-list">
          {loading ? (
            <div className="panel p-8 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
              <Loader2 size={16} className="animate-spin"/> Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="panel p-8 text-center text-muted-foreground" data-testid="misc-empty">
              <ScrollText size={22} className="mx-auto mb-3"/>
              Aún no tienes consultas guardadas.
              <div className="mt-4">
                <Link to="/app/asistente-ia" className="btn-primary inline-flex text-xs py-2" data-testid="misc-cta-new">
                  <Sparkles size={12} className="mr-1"/> Hacer mi primera consulta
                </Link>
              </div>
            </div>
          ) : items.map((it) => (
            <button
              key={it.id}
              onClick={() => openDetail(it)}
              className={`w-full text-left panel p-4 transition-colors ${selected?.id === it.id ? "ring-1 ring-emerald-400 border-emerald-400/40" : "hover:border-emerald-400/30"}`}
              data-testid={`misc-item-${it.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="pill text-[10px] badge-cyan">{it.task_label}</span>
                <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Calendar size={10}/> {fmtDate(it.created_at)}</span>
              </div>
              <div className="mt-2 text-sm font-medium line-clamp-2">{it.title}</div>
            </button>
          ))}
        </div>

        {/* Detalle */}
        <div className="lg:col-span-3" data-testid="misc-detail">
          {!selected ? (
            <div className="panel p-8 text-center text-muted-foreground h-full flex flex-col items-center justify-center min-h-[280px]">
              <ArrowRight size={22} className="mb-3 rotate-180"/>
              Selecciona una consulta del listado para verla y re-descargarla.
            </div>
          ) : detailLoading ? (
            <div className="panel p-8 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
              <Loader2 size={16} className="animate-spin"/> Cargando detalle…
            </div>
          ) : (
            <div className="panel p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="label-eyebrow text-emerald-300">{selected.task_label}</div>
                  <h2 className="serif text-2xl mt-1 leading-snug">{selected.title}</h2>
                  <div className="text-[11px] text-muted-foreground mt-2 inline-flex items-center gap-1"><Calendar size={10}/> {fmtDate(selected.created_at)}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    className="btn-ghost text-xs py-1.5 border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
                    onClick={() => exportItem(selected, "pdf")}
                    disabled={exporting !== null}
                    data-testid={`misc-export-pdf-${selected.id}`}
                  >
                    {exporting === `${selected.id}-pdf`
                      ? <><Loader2 size={12} className="animate-spin mr-1"/> Generando…</>
                      : <><FileDown size={12} className="mr-1"/> PDF</>}
                  </button>
                  <button
                    className="btn-ghost text-xs py-1.5 border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
                    onClick={() => exportItem(selected, "docx")}
                    disabled={exporting !== null}
                    data-testid={`misc-export-docx-${selected.id}`}
                  >
                    {exporting === `${selected.id}-docx`
                      ? <><Loader2 size={12} className="animate-spin mr-1"/> Generando…</>
                      : <><Download size={12} className="mr-1"/> Word</>}
                  </button>
                  <button
                    className="btn-ghost text-xs py-1.5 text-red-300 hover:text-red-200"
                    onClick={() => removeItem(selected)}
                    disabled={deletingId === selected.id}
                    data-testid={`misc-delete-${selected.id}`}
                  >
                    {deletingId === selected.id
                      ? <><Loader2 size={12} className="animate-spin mr-1"/> …</>
                      : <><Trash2 size={12} className="mr-1"/> Eliminar</>}
                  </button>
                </div>
              </div>

              <details className="mt-5 group">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">
                  Ver antecedentes originales
                </summary>
                <div className="mt-2 p-3 rounded-md bg-white/[0.03] border border-white/10 text-xs text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto">
                  {selected.context || "—"}
                </div>
              </details>

              <div className="mt-5">
                <div className="label-eyebrow text-emerald-300 mb-2 inline-flex items-center gap-1.5">
                  <BookMarked size={11}/> Respuesta de Colega IA
                </div>
                <div className="colega-ia-output panel p-5 border-emerald-400/20 bg-emerald-400/[0.02]" data-testid="misc-output-rendered">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{selected.output || ""}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
